import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Email, Phone, Facebook, Instagram, Twitter, YouTube, Menu } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const navLinks = [
  { label: 'HOME', id: 'home' },
  { label: 'ABOUT', id: 'about' },
  { label: 'FEATURES', id: 'features' },
  { label: 'SCREENSHOT', id: 'interface' },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        el?.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ fontSize: 16, color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white', fontSize: '14px' }}>
                  info@homeslygroup.com
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ fontSize: 16, color: 'white' }} />
                <Typography variant="body2" sx={{ color: 'white', fontSize: '14px' }}>
                  02045259955
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              {[Facebook, Instagram, Twitter, YouTube].map((Icon, index) => (
                <IconButton key={index} sx={{ color: 'white', p: 0.5 }}>
                  <Icon sx={{ fontSize: 20 }} />
                </IconButton>
              ))}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: 240,
            backgroundColor: '#fff',
          },
        }}
      >
        <List>
          {navLinks.map((link) => (
            <ListItem
              key={link.label}
              onClick={() => {
                scrollToSection(link.id);
                setMobileOpen(false);
              }}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{
                  fontWeight: 500,
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                }}
              />
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box
        sx={{
          backgroundColor: 'rgba(255,255,255,0.95)',
          mx: { xs: 1, sm: 2 },
          borderRadius: '12px',
          mb: { xs: 2, sm: 4 },
          position: 'relative',
          zIndex: 2,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <IconButton sx={{ color: '#333', p: 1 }} onClick={handleDrawerToggle}>
                <Menu />
              </IconButton>

              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  px: 3,
                  py: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#333',
                    fontSize: '1.2rem',
                  }}
                >
                  Homesly
                </Typography>
              </Box>

              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#6366f1',
                  color: 'white',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: '12px',
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: '#5855eb',
                  },
                }}
                onClick={() => navigate('/support')}
              >
                Book
              </Button>
            </Box>

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {navLinks.map((link) => (
                  <Button
                    key={link.id}
                    sx={{
                      color: link.label === 'HOME' ? '#6366f1' : '#666',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '14px',
                    }}
                    onClick={() => scrollToSection(link.id)}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>

              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: '20px',
                  px: 3,
                  py: 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#333',
                    fontSize: '1.5rem',
                  }}
                >
                  Homesly
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    fontSize: '14px',
                  }}
                  onClick={() => scrollToSection('interface')}
                >
                  SCREENSHOT
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#6366f1',
                    color: 'white',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    fontSize: '14px',
                    px: 3,
                    py: 1,
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: '#5855eb',
                    },
                  }}
                  onClick={() => navigate('/support')}
                >
                  BOOK A DEMO
                </Button>
              </Box>
            </Box>
          </Toolbar>
        </Container>
      </Box>
    </>
  );
};

export default Navbar;
